// Iter · Feb 2026 · R268ggg — Content Calendar Gantt view.
//
// A compact hierarchical Gantt where every deliverable is a parent row
// and its linked tasks are child rows. Drag the parent bar to change
// the deliverable's `planned_date`, drag a child bar to change the
// task's `due_date`. Data is the same enriched content-items payload
// the rest of Content Calendar already uses — no backend changes.
//
// Interaction: pointer-based dragging (works on mouse + touch). The
// bar snaps to day boundaries; the API is called on pointerup. During
// drag we render a ghost bar to preview the target date so the user
// gets immediate feedback.
//
// The left pane is a compact table (Title · Brand · Client · Project ·
// Package · Assignee) with sortable columns, a search box that greps
// across title/brand/client/project/package, and stacked filter chips.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { ChevronDown, ChevronRight, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronsLeft, ChevronsRight, Layers, Maximize2, Minimize2, Columns3, Pin, PinOff, GripVertical, Check } from "lucide-react";
import MultiSelect from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { STATUS_TONES, ITEM_STATUSES } from "@/pages/app/ContentCalendar";
import { CATEGORIES } from "@/pages/app/Packages";

// ------------------------------------------------------------------
// Date utilities (kept local to avoid pulling a heavy date lib for a
// single view). Every date is a plain ISO "YYYY-MM-DD" string; the
// UTC-safe parser below is deliberate — `new Date("2026-02-05")` gets
// parsed as UTC midnight, which surfaces as the "previous day" for
// negative-offset users. We handle everything as local calendar days.
// ------------------------------------------------------------------
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const daysBetween = (a, b) => Math.round((b - a) / 86400000);
const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isWeekend = (d) => { const w = d.getDay(); return w === 0 || w === 6; };

// ------------------------------------------------------------------
// Layout tokens. Anything visual that might need tweaking lives here.
// ------------------------------------------------------------------
const ROW_H = 26;              // px per row
const HEADER_H = 52;           // px header (month + day rows)
const BAR_H_ITEM = 18;         // px deliverable bar
const BAR_H_TASK = 12;         // px task bar
const CHEV_W = 24;             // px for the expand-chevron gutter

// R268iii · Feb 2026 — Default column config for the left pane. Every
// column is resizable & selectable. `pinned` columns can't be toggled
// off from the picker (locked visible). Order here = display order.
// R268aaaa · Feb 2026 — Content Calendar Gantt columns.
// `primary` is the Deliverable Primary Owner (kept from R268uuu).
// R268aaaa introduces 5 new inline-editable columns (Del.Status /
// Del.Date / Task.Status / Task.Start / Task.Due) — surfaced via the
// existing Columns picker so PMs can enable them per their workflow.
const DEFAULT_COLUMNS = [
  { key: "title",       label: "Title",     width: 220, visible: true,  pinned: true  },
  { key: "primary",     label: "Owner",     width: 110, visible: true,  pinned: false },
  { key: "assignee",    label: "Task Assg", width: 110, visible: true,  pinned: false },
  { key: "brand",       label: "Brand",     width: 100, visible: true,  pinned: false },
  { key: "client",      label: "Client",    width: 100, visible: false, pinned: false },
  { key: "project",     label: "Project",   width: 100, visible: false, pinned: false },
  { key: "package",     label: "Package",   width: 100, visible: false, pinned: false },
  // Iter · Feb 2026 — Category column surfaces the deliverable's
  // activity bucket (reel / post / story / …) in the left pane. Off by
  // default so existing users keep their layout; toggle via Columns.
  { key: "category",    label: "Category",  width: 90,  visible: false, pinned: false },
  // Feb 2026 · Topic column — surfaces the deliverable's content-bucket
  // topic (bold when linked to a bucket) or free-text topic (italic
  // muted) in the Gantt's left pane. Off by default so existing users
  // keep their layout; toggle via Columns. Task rows inherit the
  // parent deliverable's topic read-only, matching how Category/Brand
  // inherit on task rows.
  { key: "topic",       label: "Topic",     width: 160, visible: false, pinned: false },
  // R268aaaa · new editable columns — off by default so existing users
  // don't get a suddenly-wider left pane. Toggle via the Columns menu.
  { key: "del_status",  label: "Del Sts",   width: 110, visible: false, pinned: false },
  { key: "del_date",    label: "Del Date",  width: 100, visible: false, pinned: false },
  { key: "task_status", label: "Task Sts",  width: 100, visible: false, pinned: false },
  { key: "task_start",  label: "Start",     width: 100, visible: false, pinned: false },
  { key: "task_due",    label: "Due",       width: 100, visible: false, pinned: false },
];
const COL_MIN_W = 60;
const COL_MAX_W = 400;

// R268hhh · Feb 2026 — Zoom / density presets. `dayW` drives every
// pixel-per-day calculation in the grid. Header formatters vary so
// a Quarter-wide Gantt still reads as a Gantt (month labels, not a
// blur of day numbers).
const ZOOM_PRESETS = {
  day:   { dayW: 30, label: "Day",   showDayNumbers: true },
  week:  { dayW: 10, label: "Week",  showDayNumbers: false },
  month: { dayW: 3,  label: "Month", showDayNumbers: false },
};

// R268hhh · Feb 2026 — Handle grabs at the edges of task span bars.
// Increased slightly beyond 5 px so it's discoverable on trackpads.
const RESIZE_HANDLE_W = 6;

// Category-based color for deliverable bars. Falls back to neutral.
const CAT_COLOR = {
  "reel": "#a855f7", "post": "#0ea5e9", "story": "#f59e0b",
  "carousel": "#6366f1", "video": "#ef4444", "photo": "#10b981",
  "blog": "#8b5cf6", "email": "#ec4899", "ad": "#f43f5e",
  "podcast": "#14b8a6", "live": "#eab308", "other": "#64748b",
};
const catColor = (c) => CAT_COLOR[String(c || "").toLowerCase()] || "#64748b";

// Task status color for child bars.
const TASK_COLOR = {
  "todo": "#94a3b8", "in_progress": "#0284c7", "review": "#a855f7",
  "blocked": "#ef4444", "done": "#10b981", "cancelled": "#71717a",
};
const taskColor = (s) => TASK_COLOR[s] || "#94a3b8";

// R268aaaa · Feb 2026 — Task status options + short labels for the
// inline editors. `ITEM_STATUSES` reused from ContentCalendar.jsx.
const TASK_STATUSES = ["pending_approval", "open", "in_progress", "review", "blocked", "done"];
const ITEM_STATUS_LABEL = {
  planned: "Planned", briefed: "Briefed", in_production: "In Prod",
  internal_qc: "Int QC", client_review: "Client Rev", on_hold: "Hold",
  blocked: "Blocked", approved: "Appr", scheduled: "Sched",
  published: "Pub", cancelled: "X",
};
const TASK_STATUS_LABEL = {
  open: "Open", in_progress: "Doing", blocked: "Blocked", done: "Done",
};

// ------------------------------------------------------------------
// The Gantt component. All logic (data grouping, sort/search, drag,
// scroll sync) is inline so callers can drop it straight into a tab.
// ------------------------------------------------------------------
export default function ContentCalendarGantt({
  wsId,
  items,            // enriched content items already filtered by the parent page
  month,            // "YYYY-MM" — used to seed the visible range
  brands = [], projects = [], packages = [], members = [],
  onOpen,           // (item) => open item drawer
  onOpenTask,       // (taskId) => open task detail
  onReloaded,       // () => parent should refetch list
}) {
  // Local Gantt-only filters (independent of the outer page filters
  // so PMs can zoom into a package/assignee without disturbing the
  // other calendar tabs).
  const [q, setQ] = useState("");
  const [clientF, setClientF] = useState([]);
  const [brandF, setBrandF] = useState([]);
  const [projectF, setProjectF] = useState([]);
  const [packageF, setPackageF] = useState([]);
  const [assigneeF, setAssigneeF] = useState([]);
  // Iter · Feb 2026 — Owner (primary assignee) filter runs independently
  // of the Task-Assignee filter. Matches the deliverable's own owner,
  // so a PM can see every row owned by one teammate even when linked
  // tasks are spread across the roster.
  const [ownerF, setOwnerF] = useState([]);
  const [statusF, setStatusF] = useState([]);
  const [catF, setCatF] = useState([]);

  const [sort, setSort] = useState({ key: "planned_date", dir: "asc" });
  const [expanded, setExpanded] = useState(() => new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [full, setFull] = useState(false);
  // R268hhh · Feb 2026 — Zoom density. `day` = 30px/day (default),
  // `week` = 10px/day (~quarter fits on screen), `month` = 3px/day
  // (~2 years fits without scrolling).
  const [zoom, setZoom] = useState("day");
  const DAY_W = ZOOM_PRESETS[zoom].dayW;

  // R268iii · Feb 2026 — Column config (order · widths · visibility ·
  // pinned). Persisted to localStorage per-workspace so a PM's chosen
  // layout survives page reloads and tab switches.
  const colStorageKey = `cc-gantt-cols::${wsId || "_"}`;
  const [columns, setColumns] = useState(() => {
    try {
      const raw = typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem(colStorageKey) : null;
      if (raw) {
        const saved = JSON.parse(raw);
        // Merge saved widths/visibility with DEFAULT_COLUMNS so a newly
        // added default column doesn't disappear for existing users.
        const byKey = new Map(saved.map((c) => [c.key, c]));
        return DEFAULT_COLUMNS.map((d) => ({ ...d, ...(byKey.get(d.key) || {}) }));
      }
    } catch { /* fall through */ }
    return DEFAULT_COLUMNS;
  });
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(colStorageKey, JSON.stringify(columns));
      }
    } catch { /* ignore quota */ }
  }, [columns, colStorageKey]);

  const visibleCols = useMemo(() => columns.filter((c) => c.visible || c.pinned), [columns]);
  const gridTemplate = useMemo(
    () => `${CHEV_W}px ` + visibleCols.map((c) => `${c.width}px`).join(" "),
    [visibleCols],
  );
  const LEFT_W = useMemo(
    () => CHEV_W + visibleCols.reduce((s, c) => s + c.width, 0),
    [visibleCols],
  );

  // Toggle a column's visibility (no-op for pinned).
  const toggleColVisible = (key) => setColumns((cols) => cols.map((c) =>
    c.key === key ? (c.pinned ? c : { ...c, visible: !c.visible }) : c));
  const toggleColPinned = (key) => setColumns((cols) => cols.map((c) =>
    c.key === key ? { ...c, pinned: !c.pinned, visible: !c.pinned ? true : c.visible } : c));
  const resetCols = () => setColumns(DEFAULT_COLUMNS);

  // Column resize — pointer-based drag on the header handle.
  const [resizing, setResizing] = useState(null); // {key, startX, startW}
  const onColResizeStart = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ key: col.key, startX: e.clientX, startW: col.width });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* no-op */ }
  }, []);
  useEffect(() => {
    if (!resizing) return;
    const mv = (e) => {
      const dx = e.clientX - resizing.startX;
      const w = Math.max(COL_MIN_W, Math.min(COL_MAX_W, resizing.startW + dx));
      setColumns((cols) => cols.map((c) => c.key === resizing.key ? { ...c, width: w } : c));
    };
    const up = () => setResizing(null);
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", mv);
      window.removeEventListener("pointerup", up);
    };
  }, [resizing]);

  // R268iii · Assignee display helpers. For a deliverable we roll up
  // the distinct set of task assignees; for a task we just use its own.
  const _itemAssignees = (it) => {
    const seen = new Set(), names = [];
    (it.tasks || []).forEach((t) => {
      const n = t.assignee_name;
      if (n && !seen.has(n)) { seen.add(n); names.push(n); }
    });
    return names;
  };

  // R268kkk · Feb 2026 — Override map keyed by id → per-field overrides.
  // Every drag/resize writes into this map so the row/bar snaps into
  // its new place instantly, before the API confirms. On failure we
  // rollback the specific field only. Shape:
  //   { [id]: { planned_date?, start_date?, due_date? } }
  const [override, setOverride] = useState({});
  // Feb 2026 · User-controlled cascade toggle for parent (deliverable)
  // drags. OFF by default (user-requested) so moving a deliverable
  // doesn't stomp on manual task dates. When ON, backend applies a
  // "smart" cascade: only tasks whose current due_date still matches
  // the old item date are re-aligned.
  const SYNC_KEY = "cc_gantt_sync_child_dates";
  const [syncChildDates, setSyncChildDates] = useState(() => {
    try { return localStorage.getItem(SYNC_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SYNC_KEY, syncChildDates ? "1" : "0"); } catch { /* private mode */ }
  }, [syncChildDates]);
  const _applyOverride = (id, patch) => setOverride((o) => ({
    ...o, [id]: { ...(o[id] || {}), ...patch }
  }));
  const _revertOverride = (id, fields) => setOverride((o) => {
    const cur = o[id] || {};
    const next = { ...cur };
    fields.forEach((f) => { delete next[f]; });
    const clone = { ...o };
    if (Object.keys(next).length) clone[id] = next; else delete clone[id];
    return clone;
  });
  // Effective ISO date for a given id/field — override wins over the
  // canonical field on the raw item/task. Used by both the sort
  // comparator and the range-fitting loop.
  const _eff = (id, field, fallback) => (override[id] && override[id][field]) || fallback;

  // ------------------------------------------------------------------
  // Derived — clients list from brands (unique by client_id/name).
  // ------------------------------------------------------------------
  const clients = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => {
      if (it.client_id) m.set(it.client_id, { id: it.client_id, name: it.client_name || "—" });
    });
    return Array.from(m.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [items]);

  // ------------------------------------------------------------------
  // Apply Gantt-local filters + search over the enriched item list.
  // ------------------------------------------------------------------
  const filteredItems = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (clientF.length && !clientF.includes(it.client_id)) return false;
      if (brandF.length && !brandF.includes(it.brand_id)) return false;
      if (projectF.length && !projectF.includes(it.project_id) && !(it.project_names || []).some((n) => projectF.includes(n))) return false;
      if (packageF.length && !packageF.includes(it.package_id)) return false;
      if (statusF.length && !statusF.includes(it.status)) return false;
      if (catF.length && !catF.includes(it.category)) return false;
      if (ownerF.length && !ownerF.includes(it.primary_assignee_user_id)) return false;
      if (assigneeF.length) {
        // Deliverable row shows if ANY linked task is assigned to a
        // selected assignee. Un-linked deliverables never match.
        const hits = (it.tasks || []).some((t) => assigneeF.includes(t.assignee_id));
        if (!hits) return false;
      }
      if (s) {
        const hay = [
          it.topic, it.name, it.title, it.brand_name, it.client_name,
          it.project_name, it.package_name, it.category,
          ...(it.task_titles || []),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [items, q, clientF, brandF, projectF, packageF, statusF, catF, ownerF, assigneeF]);

  // ------------------------------------------------------------------
  // R268jjj · Feb 2026 — Special / important days (global catalog +
  // workspace-specific custom days). Used to shade the day-header,
  // draw the weekend/holiday column bg, and drop thin vertical tick
  // marks at the week/month zoom densities. Fetched once per visible
  // year so a Gantt showing Dec-2025 → Feb-2026 pulls two years.
  // ------------------------------------------------------------------
  const [specialByIso, setSpecialByIso] = useState(new Map());
  useEffect(() => {
    if (!wsId) return;
    const anchorY = Number((month || iso(new Date())).slice(0, 4));
    // Anchor ± 1 year covers the widest reasonable zoom-out span
    // (~6000 px / 3 px per day ≈ 5.5 years, but 3 years worth of
    // holidays is already plenty for the ± 180 day common case).
    const years = [anchorY - 1, anchorY, anchorY + 1];
    let cancelled = false;
    Promise.all(years.flatMap((y) => ([
      api.get(`/reference/special-days?year=${y}`).then((r) => r.data?.days || []).catch(() => []),
      api.get(`/workspaces/${wsId}/custom-days?year=${y}`).then((r) => r.data || []).catch(() => []),
    ]))).then((chunks) => {
      if (cancelled) return;
      const map = new Map();
      chunks.flat().forEach((d) => {
        if (!d?.date) return;
        const key = String(d.date).slice(0, 10);
        const arr = map.get(key) || [];
        arr.push({ name: d.name, category: d.category, emoji: d.emoji });
        map.set(key, arr);
      });
      setSpecialByIso(map);
    });
    return () => { cancelled = true; };
  }, [wsId, month]);

  // Per-day metadata used by the timeline body & header. Kept as a
  // pure fn (not memoized) — the day loop below is small.
  const _dayMeta = (d) => {
    const dw = d.getDay();
    const isWkend = dw === 0 || dw === 6;
    const key = iso(d);
    const specials = specialByIso.get(key) || [];
    const isSpecial = specials.length > 0;
    return { isWkend, isSpecial, specials, key };
  };
  // Same shading rules as the Timeline tab (R230): weekend = gray,
  // special = yellow, both = orange. Returns an inline style so we can
  // paint SVG/absolute overlays as well as header cells.
  const _dayBg = (m, alpha = 1) => {
    // Hex triplets tuned to match the tailwind classes used in
    // ContentCalendarTimeline (`bg-gray-100`, `bg-yellow-100`,
    // `bg-orange-100`). `alpha` lets the timeline body overlay use a
    // softer wash while headers stay saturated.
    if (m.isWkend && m.isSpecial) return `rgba(254, 215, 170, ${alpha})`;
    if (m.isSpecial) return `rgba(254, 240, 138, ${alpha})`;
    if (m.isWkend) return `rgba(226, 232, 240, ${alpha})`;
    return null;
  };

  // ------------------------------------------------------------------
  // Sort deliverables by the active key. Null/empty always sink to the
  // bottom so unrouted rows don't hijack the top of a busy grid.
  // ------------------------------------------------------------------
  const sortedItems = useMemo(() => {
    if (!sort.key || !sort.dir) return filteredItems;
    const dir = sort.dir === "asc" ? 1 : -1;
    const getV = (it) => {
      switch (sort.key) {
        case "title": return (it.topic || it.name || it.title || "").toLowerCase();
        case "brand": return (it.brand_name || "").toLowerCase();
        case "client": return (it.client_name || "").toLowerCase();
        case "project": return (it.project_name || (it.project_names || [])[0] || "").toLowerCase();
        case "package": return (it.package_name || "").toLowerCase();
        case "status": return it.status || "";
        case "planned_date": return _eff(it.id, "planned_date", it.planned_date) || "";
        default: return "";
      }
    };
    return [...filteredItems].sort((a, b) => {
      const av = getV(a), bv = getV(b);
      const an = !av; const bn = !bv;
      if (an && bn) return 0;
      if (an) return 1;
      if (bn) return -1;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return dir * cmp;
    });
  }, [filteredItems, sort, override]);

  // ------------------------------------------------------------------
  // Flatten to rows: [{kind:'item', it}, {kind:'task', task, parent}, …]
  // A deliverable always renders, its tasks only when expanded.
  // ------------------------------------------------------------------
  const rows = useMemo(() => {
    const out = [];
    sortedItems.forEach((it) => {
      out.push({ kind: "item", key: it.id, it });
      const showKids = expandAll || expanded.has(it.id);
      if (showKids) {
        (it.tasks || []).forEach((t) => {
          if (assigneeF.length && !assigneeF.includes(t.assignee_id)) return;
          out.push({ kind: "task", key: `${it.id}::${t.id}`, task: t, parent: it });
        });
      }
    });
    return out;
  }, [sortedItems, expanded, expandAll, assigneeF]);

  // ------------------------------------------------------------------
  // Visible date range. Anchor on the passed month (+/- 7 days padding)
  // and stretch to fit any deliverable/task that lands outside it.
  // ------------------------------------------------------------------
  const [yy, mm] = (month || iso(new Date()).slice(0, 7)).split("-").map(Number);
  const anchorStart = new Date(yy, mm - 1, 1);
  const anchorEnd = new Date(yy, mm, 0); // last day of month
  const range = useMemo(() => {
    let s = addDays(anchorStart, -7);
    let e = addDays(anchorEnd, 7);
    filteredItems.forEach((it) => {
      const pd = parseISO(_eff(it.id, "planned_date", it.planned_date));
      if (pd) {
        if (pd < s) s = pd;
        if (pd > e) e = pd;
      }
      (it.tasks || []).forEach((t) => {
        const td = parseISO(_eff(t.id, "due_date", t.due_date));
        if (td) {
          if (td < s) s = td;
          if (td > e) e = td;
        }
        const ts = parseISO(_eff(t.id, "start_date", t.start_date));
        if (ts) {
          if (ts < s) s = ts;
          if (ts > e) e = ts;
        }
      });
    });
    // Cap the total span at ~6000 px so a stray 2028 due-date doesn't
    // stretch the DOM to tens of thousands of pixels. The cap scales
    // with zoom so month density can still show 4-5 years comfortably.
    const maxDays = Math.max(60, Math.round(6000 / DAY_W));
    const totalDays = daysBetween(s, e);
    if (totalDays > maxDays) e = addDays(s, maxDays);
    return { start: s, end: e, days: daysBetween(s, e) + 1 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, override, month, DAY_W]);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < range.days; i++) out.push(addDays(range.start, i));
    return out;
  }, [range]);

  // Group day columns by month for the top row.
  const monthGroups = useMemo(() => {
    const out = [];
    let cur = null;
    days.forEach((d, i) => {
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      if (!cur || cur.k !== k) {
        cur = { k, label: d.toLocaleString("en", { month: "short", year: "2-digit" }), start: i, count: 1 };
        out.push(cur);
      } else {
        cur.count += 1;
      }
    });
    return out;
  }, [days]);

  const totalGridW = range.days * DAY_W;
  const today = new Date();
  const todayIdx = (today >= range.start && today <= range.end) ? daysBetween(range.start, today) : -1;

  // ------------------------------------------------------------------
  // Drag state. `drag = null` when idle. During a drag we mirror the
  // pointer delta in `drag.deltaDays` so the ghost bar snaps live.
  // ------------------------------------------------------------------
  // R268kkk · Feb 2026 — Drag state now carries `mode` ("move" |
  // "resize-left" | "resize-right") so the same pointer plumbing
  // powers both dragging a bar and dragging one of its edge handles.
  // `originStart` / `originEnd` are the ISO dates captured at
  // pointerdown; the pointerup handler diffs against them to build
  // the PATCH body.
  const [drag, setDrag] = useState(null);
  const scrollRef = useRef(null);

  const onBarPointerDown = useCallback((e, row, mode = "move") => {
    // Only main button
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (row.kind === "item") {
      const id = row.it.id;
      const originEnd = _eff(id, "planned_date", row.it.planned_date);
      if (!originEnd) return;
      setDrag({ rowKey: row.key, id, kind: "item", mode: "move",
                startX: e.clientX, deltaDays: 0,
                originStart: originEnd, originEnd });
    } else {
      const id = row.task.id;
      const originEnd = _eff(id, "due_date", row.task.due_date);
      const originStart = _eff(id, "start_date", row.task.start_date) || originEnd;
      if (!originEnd) return;
      setDrag({ rowKey: row.key, id, kind: "task", mode,
                startX: e.clientX, deltaDays: 0,
                originStart, originEnd });
    }
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* touch may throw */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override]);

  const onBarPointerMove = useCallback((e) => {
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dd = Math.round(dx / DAY_W);
    if (dd !== drag.deltaDays) setDrag((d) => (d ? { ...d, deltaDays: dd } : d));
  }, [drag, DAY_W]);

  const onBarPointerUp = useCallback(async () => {
    if (!drag) return;
    const { rowKey, id, kind, mode, deltaDays, originStart, originEnd } = drag;
    setDrag(null);
    // R268ppp · Feb 2026 — Bar click (no drag) → open the same edit
    // popup the row itself opens. Distinguishes click from drag by
    // `deltaDays === 0` on pointerup. Resize handles never open the
    // popup (they exist only for dragging).
    if (!deltaDays) {
      if (mode === "move") {
        const row = rows.find((r) => r.key === rowKey);
        if (row) {
          if (row.kind === "item" && onOpen) onOpen(row.it);
          else if (row.kind === "task" && onOpenTask) onOpenTask(row.task.id);
        }
      }
      return;
    }

    // Compute the new dates + PATCH body based on drag mode. Every
    // path clamps so start_date never crosses due_date.
    let patch = {}; let fields = [];
    if (kind === "item") {
      const newDate = iso(addDays(parseISO(originEnd), deltaDays));
      patch = { planned_date: newDate };
      fields = ["planned_date"];
      _applyOverride(id, { planned_date: newDate });
    } else if (mode === "resize-left") {
      let ns = addDays(parseISO(originStart), deltaDays);
      const ne = parseISO(originEnd);
      if (ns > ne) ns = ne;
      const newStart = iso(ns);
      patch = { start_date: newStart };
      fields = ["start_date"];
      _applyOverride(id, { start_date: newStart });
    } else if (mode === "resize-right") {
      const ns = parseISO(originStart);
      let ne = addDays(parseISO(originEnd), deltaDays);
      if (ne < ns) ne = ns;
      const newEnd = iso(ne);
      patch = { due_date: newEnd };
      fields = ["due_date"];
      _applyOverride(id, { due_date: newEnd });
    } else {
      // Task move — if a span, slide BOTH dates so the length is
      // preserved. If point (no start_date), only slide due_date.
      const hasSpan = originStart && originStart !== originEnd;
      const newEnd = iso(addDays(parseISO(originEnd), deltaDays));
      if (hasSpan) {
        const newStart = iso(addDays(parseISO(originStart), deltaDays));
        patch = { start_date: newStart, due_date: newEnd };
        fields = ["start_date", "due_date"];
        _applyOverride(id, { start_date: newStart, due_date: newEnd });
      } else {
        patch = { due_date: newEnd };
        fields = ["due_date"];
        _applyOverride(id, { due_date: newEnd });
      }
    }

    try {
      if (kind === "item") {
        // Feb 2026 · Pass the user's "Sync child dates" preference so the
        // backend knows whether to cascade the new planned_date to
        // linked tasks. See routes_content_calendar.patch_item for the
        // "none" vs "smart" vs "all" contract.
        const mode = syncChildDates ? "smart" : "none";
        await api.patch(`/content-items/${id}?cascade_tasks=${mode}`, patch);
        toast.success(`Moved deliverable to ${patch.planned_date}${syncChildDates ? " · aligned tasks" : ""}`);
      } else {
        // R94 backend rule — any real date change on a task requires a
        // justification comment. The Gantt is a drag surface where a
        // free-text prompt would kill the flow, so we auto-generate a
        // sensible reason (the operation + the new date range). Users
        // wanting a richer explanation can still edit the task detail
        // dialog directly.
        const dateChangeReason = mode === "resize-left"
          ? `Gantt resize · start → ${patch.start_date}`
          : mode === "resize-right"
          ? `Gantt resize · due → ${patch.due_date}`
          : (patch.start_date && patch.due_date)
          ? `Gantt drag · ${patch.start_date} → ${patch.due_date}`
          : `Gantt drag · due → ${patch.due_date}`;
        await api.patch(`/tasks/${id}`, { ...patch, date_change_reason: dateChangeReason });
        const summary = mode === "resize-left" ? `Task start ${patch.start_date}`
                       : mode === "resize-right" ? `Task due ${patch.due_date}`
                       : (patch.start_date && patch.due_date) ? `Task ${patch.start_date} → ${patch.due_date}`
                       : `Task due ${patch.due_date}`;
        toast.success(summary);
      }
      if (onReloaded) onReloaded();
    } catch (err) {
      _revertOverride(id, fields);
      toast.error(apiErrorMessage(err, "Reschedule failed"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, onReloaded, rows, onOpen, onOpenTask, syncChildDates]);

  // Attach global pointermove/up so drag continues even if the pointer
  // leaves the bar rectangle mid-drag.
  useEffect(() => {
    if (!drag) return;
    const mv = (e) => onBarPointerMove(e);
    const up = (e) => onBarPointerUp(e);
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", mv);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, onBarPointerMove, onBarPointerUp]);

  const toggleRow = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const handleSort = (k) => setSort((p) => {
    if (p.key !== k) return { key: k, dir: "asc" };
    if (p.dir === "asc") return { key: k, dir: "desc" };
    return { key: null, dir: null };
  });

  const scrollToToday = () => {
    const el = scrollRef.current;
    if (!el || todayIdx < 0) return;
    el.scrollLeft = Math.max(0, todayIdx * DAY_W - el.clientWidth * 0.35);
  };
  useEffect(() => { scrollToToday(); /* eslint-disable-next-line */ }, [range.start.getTime()]);

  // R268aaaa · Feb 2026 — Scroll the timeline to a specific date so the
  // per-row "go to date" arrow can jump straight to a deliverable's or
  // task's planned span even when it's off-screen.
  const scrollToDate = useCallback((iso) => {
    const el = scrollRef.current;
    if (!el || !iso) return;
    const d = parseISO(iso);
    if (!d) return;
    const idx = daysBetween(range.start, d);
    if (idx < 0 || idx > range.days) return;
    el.scrollLeft = Math.max(0, idx * DAY_W - el.clientWidth * 0.3);
  }, [range.start, range.days, DAY_W]);

  // R268aaaa · Feb 2026 — Inline patch helpers for the new editable
  // columns. Deliverables PATCH /content-items/{cid}, tasks PATCH
  // /tasks/{tid}. `onReloaded` refreshes the parent state so bar
  // positions and category-color chips stay in sync with the grid.
  const patchItem = useCallback(async (cid, body) => {
    try {
      // Feb 2026 · Honour the Sync toggle for inline Del-Date edits too,
      // not just drag — so a user toggling OFF then editing the date
      // column gets the same no-cascade behaviour.
      const mode = syncChildDates ? "smart" : "none";
      await api.patch(`/content-items/${cid}?cascade_tasks=${mode}`, body);
      onReloaded && onReloaded();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  }, [onReloaded, syncChildDates]);
  const patchTask = useCallback(async (tid, body) => {
    try {
      await api.patch(`/tasks/${tid}`, body);
      onReloaded && onReloaded();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  }, [onReloaded]);

  // R268aaaa · Feb 2026 — Left/right pane resize handle. The user
  // drags a vertical grip between the two panes to reallocate width
  // between the row grid (item/task metadata) and the timeline. The
  // computed `leftPaneWidth` is applied as an inline flex-basis on
  // the left pane (min 260, max 900); when null (default) the pane
  // sizes itself to the visible-columns' widths.
  const [leftPaneWidth, setLeftPaneWidth] = useState(null);
  const [paneResizing, setPaneResizing] = useState(null);
  const onPaneResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setPaneResizing({ startX: e.clientX, startW: leftPaneWidth ?? LEFT_W });
  }, [leftPaneWidth, LEFT_W]);
  useEffect(() => {
    if (!paneResizing) return;
    const mv = (evt) => {
      const dx = evt.clientX - paneResizing.startX;
      const w = Math.max(260, Math.min(900, paneResizing.startW + dx));
      setLeftPaneWidth(w);
    };
    const up = () => setPaneResizing(null);
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", mv);
      window.removeEventListener("pointerup", up);
    };
  }, [paneResizing]);

  const SortIcon = ({ colKey }) => {
    if (sort.key !== colKey) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sort.dir === "asc" ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />;
  };

  // Deliverable/task effective start & end ISO dates. For items and
  // point-only tasks start === end (a single-day marker). For task
  // spans they can differ.
  const _rowSpan = (row) => {
    if (row.kind === "item") {
      const p = _eff(row.it.id, "planned_date", row.it.planned_date);
      return { start: p, end: p };
    }
    const t = row.task;
    const end = _eff(t.id, "due_date", t.due_date);
    const start = _eff(t.id, "start_date", t.start_date) || end;
    // Guard against inverted spans (belt & braces — the drag handler
    // already clamps, but a bad DB value shouldn't crash the render).
    if (start && end && start > end) return { start: end, end };
    return { start, end };
  };

  return (
    <div className={"border border-border bg-background " + (full ? "fixed inset-0 z-50 overflow-hidden" : "")}
         data-testid="cc-gantt-root">
      {/* Toolbar: search + filters + expand/collapse + scroll-to-today + fullscreen */}
      <div className="flex items-center gap-2 flex-wrap p-2 border-b border-border bg-muted/30">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, brand, task…"
                 className="h-8 rounded-none pl-7 w-56" data-testid="cc-gantt-search" />
        </div>
        <MultiSelect options={clients.map((c) => ({ value: c.id, label: c.name }))}
                     value={clientF} onChange={setClientF} allLabel="All clients" placeholder="Client" width="w-40" testid="cc-gantt-client-filter" />
        <MultiSelect options={brands.map((b) => ({ value: b.id, label: b.name }))}
                     value={brandF} onChange={setBrandF} allLabel="All brands" placeholder="Brand" width="w-40" testid="cc-gantt-brand-filter" />
        <MultiSelect options={projects.map((p) => ({ value: p.id, label: p.name }))}
                     value={projectF} onChange={setProjectF} allLabel="All projects" placeholder="Project" width="w-40" testid="cc-gantt-project-filter" />
        <MultiSelect options={packages.map((p) => ({ value: p.id, label: p.name }))}
                     value={packageF} onChange={setPackageF} allLabel="All packages" placeholder="Package" width="w-40" testid="cc-gantt-package-filter" />
        <MultiSelect options={members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email }))}
                     value={ownerF} onChange={setOwnerF} allLabel="All owners" placeholder="Owner" width="w-40" testid="cc-gantt-owner-filter" />
        <MultiSelect options={members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email }))}
                     value={assigneeF} onChange={setAssigneeF} allLabel="All assignees" placeholder="Assignee" width="w-40" testid="cc-gantt-assignee-filter" />
        <MultiSelect options={ITEM_STATUSES.map((s) => ({ value: s, label: s.replaceAll("_", " ") }))}
                     value={statusF} onChange={setStatusF} allLabel="All statuses" placeholder="Status" width="w-36" testid="cc-gantt-status-filter" />
        <MultiSelect options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                     value={catF} onChange={setCatF} allLabel="All categories" placeholder="Category" width="w-36" testid="cc-gantt-cat-filter" />
        {/* Iter · Feb 2026 — Tiny colour-key popover so new PMs can
            learn the category palette without digging into the code.
            Lists every CAT_COLOR entry alongside the swatch. */}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button"
                    className="h-8 w-8 flex items-center justify-center border border-border rounded-none hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                    title="Category colour legend"
                    data-testid="cc-gantt-cat-legend-btn">
              <span className="w-3.5 h-3.5 rounded-full relative"
                    style={{ background: "conic-gradient(#a855f7 0 25%, #0ea5e9 25% 50%, #f59e0b 50% 75%, #10b981 75% 100%)" }} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-0 rounded-none" data-testid="cc-gantt-cat-legend-panel">
            <div className="px-3 py-2 border-b border-border bg-muted/40">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Category colours</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">Used by chips, dots & bars</div>
            </div>
            <ul className="divide-y divide-border/60 max-h-72 overflow-auto">
              {Object.entries(CAT_COLOR).map(([k, c]) => (
                <li key={k}
                    className="px-3 py-1.5 flex items-center gap-2 text-[11px]"
                    data-testid={`cc-gantt-cat-legend-${k}`}>
                  <span className="inline-block w-3 h-3 rounded-full shrink-0 border border-black/5" style={{ background: c }} />
                  <span className="capitalize truncate flex-1">{k}</span>
                  <span className="font-mono text-[9px] text-muted-foreground">{c}</span>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
        <div className="ml-auto flex items-center gap-1">
          {/* Feb 2026 · Sync-child-dates toggle. OFF by default so a
               parent drag never clobbers manual task dates. ON applies
               the backend's smart cascade (only aligned tasks move). */}
          <label
            className="inline-flex items-center gap-1 h-8 px-2 border border-border text-[11px] cursor-pointer select-none hover:bg-muted/60"
            title={syncChildDates
              ? "ON: Moving a deliverable also moves its linked tasks that are still aligned to the old date."
              : "OFF: Moving a deliverable does NOT change its linked tasks (you keep manual control)."}
            data-testid="cc-gantt-sync-child-toggle"
          >
            <input
              type="checkbox"
              className="rounded-none accent-primary"
              checked={syncChildDates}
              onChange={(e) => setSyncChildDates(e.target.checked)}
              data-testid="cc-gantt-sync-child-checkbox"
            />
            Sync child dates
          </label>
          {/* R268hhh · Zoom density switcher — Day / Week / Month */}
          <Select value={zoom} onValueChange={setZoom}>
            <SelectTrigger className="h-8 w-28 rounded-none" data-testid="cc-gantt-zoom">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ZOOM_PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={"cc-gantt-zoom-" + k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* R268iii · Columns picker — visibility + pinning */}
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 rounded-none" data-testid="cc-gantt-columns-btn">
                <Columns3 className="w-3.5 h-3.5 mr-1" /> Columns
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0 rounded-none">
              <div className="p-2 border-b border-border flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Left-pane columns</span>
                <button onClick={resetCols} className="text-[10px] text-primary hover:underline" data-testid="cc-gantt-cols-reset">Reset</button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {columns.map((col) => (
                  <div key={col.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 border-b border-border/40 last:border-b-0"
                       data-testid={"cc-gantt-col-row-" + col.key}>
                    <button
                      onClick={() => toggleColVisible(col.key)}
                      disabled={col.pinned}
                      className={"w-4 h-4 border border-border flex items-center justify-center " + (col.pinned ? "bg-muted cursor-not-allowed" : "hover:bg-primary/10")}
                      title={col.pinned ? "Pinned — always visible" : (col.visible ? "Hide" : "Show")}
                      data-testid={"cc-gantt-col-toggle-" + col.key}>
                      {(col.visible || col.pinned) && <Check className="w-3 h-3 text-primary" />}
                    </button>
                    <span className={"text-[12px] flex-1 " + ((col.visible || col.pinned) ? "text-foreground" : "text-muted-foreground/60")}>{col.label}</span>
                    <span className="text-[9px] font-mono text-muted-foreground w-10 text-right">{col.width}px</span>
                    <button
                      onClick={() => toggleColPinned(col.key)}
                      className={"p-0.5 " + (col.pinned ? "text-primary" : "text-muted-foreground/50 hover:text-foreground")}
                      title={col.pinned ? "Unpin" : "Pin (locks visibility)"}
                      data-testid={"cc-gantt-col-pin-" + col.key}>
                      {col.pinned ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ))}
              </div>
              <div className="px-2 py-1.5 border-t border-border text-[9px] text-muted-foreground">
                Drag the right edge of any column header to resize.
              </div>
            </PopoverContent>
          </Popover>
          <Button size="sm" variant="outline" className="h-8 rounded-none" onClick={() => setExpandAll((v) => !v)} data-testid="cc-gantt-expand-all">
            <Layers className="w-3.5 h-3.5 mr-1" />{expandAll ? "Collapse all" : "Expand all"}
          </Button>
          <Button size="sm" variant="outline" className="h-8 rounded-none" onClick={scrollToToday} data-testid="cc-gantt-today"
                  disabled={todayIdx < 0} title="Scroll to today">Today</Button>
          <Button size="sm" variant="outline" className="h-8 rounded-none" onClick={() => setFull((v) => !v)} data-testid="cc-gantt-fullscreen">
            {full ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Grid — two panes sharing a horizontal ruler */}
      <div className="flex" style={{ maxHeight: full ? "calc(100vh - 64px)" : "72vh" }}>
        {/* LEFT PANE */}
        <div className="border-r border-border bg-background overflow-hidden"
             style={{ width: leftPaneWidth ?? LEFT_W, minWidth: leftPaneWidth ?? LEFT_W }}>
          <div className="grid text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-muted/60 border-b border-border sticky top-0 z-20"
               style={{ height: HEADER_H, alignContent: "end", gridTemplateColumns: gridTemplate }}>
            <div />
            {visibleCols.map((col) => (
              <div key={col.key} className="relative flex items-center pb-1"
                   data-testid={"cc-gantt-hdr-" + col.key}>
                <button onClick={() => handleSort(col.key)}
                        className="flex items-center gap-1 px-2 hover:text-primary text-left flex-1 min-w-0 truncate"
                        data-testid={"cc-gantt-sort-" + col.key}
                        title={col.pinned ? col.label + " (pinned)" : col.label}>
                  <span className="truncate">{col.label}</span>
                  <SortIcon colKey={col.key} />
                  {col.pinned && <Pin className="w-2.5 h-2.5 text-primary/70 shrink-0" />}
                </button>
                {/* Resize handle — 6 px wide grab strip on the right edge */}
                <div
                  onPointerDown={(e) => onColResizeStart(e, col)}
                  className="absolute top-0 right-0 h-full cursor-col-resize hover:bg-primary/20 active:bg-primary/40"
                  style={{ width: 6 }}
                  data-testid={"cc-gantt-col-resize-" + col.key}
                  title="Drag to resize" />
              </div>
            ))}
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: full ? "calc(100vh - 64px - " + HEADER_H + "px)" : "calc(72vh - " + HEADER_H + "px)" }}
               id="cc-gantt-left-scroll"
               onScroll={(e) => {
                 // R268aaaa · Feb 2026 — Reverse-direction scroll sync.
                 // Previously only right→left was wired, so scrolling the
                 // left pane's row list vertically left the timeline
                 // frozen behind it. Guarded compare avoids the loop
                 // that would otherwise be triggered by the right
                 // pane's onScroll re-setting our scrollTop.
                 const right = document.getElementById("cc-gantt-right-scroll");
                 if (right && Math.abs(right.scrollTop - e.currentTarget.scrollTop) > 0.5) {
                   right.scrollTop = e.currentTarget.scrollTop;
                 }
               }}>
            {rows.length === 0 ? (
              <div className="text-xs text-muted-foreground p-6 text-center" data-testid="cc-gantt-empty">
                No deliverables match your filters.
              </div>
            ) : rows.map((row) => {
              if (row.kind === "item") {
                const it = row.it;
                const hasKids = (it.tasks || []).length > 0;
                const isOpen = expandAll || expanded.has(it.id);
                const assignees = _itemAssignees(it);
                return (
                  <div key={row.key}
                       className="grid items-center border-b border-border/60 hover:bg-primary/5 group cursor-pointer"
                       style={{ height: ROW_H, gridTemplateColumns: gridTemplate }}
                       data-testid={"cc-gantt-row-item-" + it.id}
                       onClick={() => onOpen && onOpen(it)}>
                    <button onClick={(e) => { e.stopPropagation(); toggleRow(it.id); }}
                            className="flex items-center justify-center text-muted-foreground hover:text-foreground"
                            disabled={!hasKids}
                            data-testid={"cc-gantt-toggle-" + it.id}>
                      {hasKids
                        ? (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)
                        : <span className="w-1 h-1 bg-muted-foreground/40 rounded-full" />}
                    </button>
                    {visibleCols.map((col) => {
                      switch (col.key) {
                        case "title": {
                          // Feb 2026 · Show the deliverable NAME as the
                          // primary text and the topic as a muted
                          // subtitle underneath, so admins see both at
                          // a glance in the Gantt row. Previously the
                          // row used `topic || name` and lost whichever
                          // of the two was less verbose.
                          const topic = it.linked_bucket?.topic_title || it.topic;
                          const primary = it.name || it.title || "Untitled";
                          return (
                            <div key={col.key} className="min-w-0 flex-1 pr-1 px-1">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: catColor(it.category) }} />
                                <span className="truncate text-[12px] font-medium" title={topic ? `${primary} · ${topic}` : primary}>
                                  {primary}
                                </span>
                                {hasKids && <span className="text-[9px] text-muted-foreground shrink-0">· {it.tasks_done || 0}/{it.tasks_total || 0}</span>}
                                {/* R268aaaa · Feb 2026 — Row scroll-to-date */}
                                <button type="button"
                                        onClick={(e) => { e.stopPropagation(); scrollToDate(it.planned_date); }}
                                        className="ml-auto opacity-0 group-hover:opacity-70 hover:!opacity-100 text-primary shrink-0"
                                        title={"Scroll to " + (it.planned_date || "—")}
                                        data-testid={"cc-gantt-goto-item-" + it.id}>
                                  <ChevronsRight className="w-3 h-3" />
                                </button>
                              </div>
                              {topic && (
                                <div className={`text-[10px] truncate flex items-center gap-1 pl-[12px] ${it.linked_bucket?.topic_title ? "text-emerald-700" : "text-muted-foreground italic"}`}
                                      title={`Topic: ${topic}`}
                                      data-testid={`cc-gantt-topic-${it.id}`}>
                                  <span className="truncate">{topic}</span>
                                </div>
                              )}
                            </div>
                          );
                        }
                        case "primary": return (
                          <span key={col.key} className="truncate text-[11px] px-1 text-muted-foreground"
                                title={it.primary_assignee_name || "Unassigned"}>
                            {it.primary_assignee_name || "—"}
                          </span>
                        );
                        case "assignee": return (
                          <span key={col.key} className="truncate text-[11px] px-1 text-muted-foreground"
                                title={assignees.join(", ") || "Unassigned"}>
                            {assignees.length === 0 ? "—"
                              : assignees.length === 1 ? assignees[0]
                              : `${assignees[0]} +${assignees.length - 1}`}
                          </span>
                        );
                        case "brand":   return <span key={col.key} className="truncate text-[11px] px-1" title={it.brand_name}>{it.brand_name || "—"}</span>;
                        case "client":  return <span key={col.key} className="truncate text-[11px] px-1 text-muted-foreground" title={it.client_name}>{it.client_name || "—"}</span>;
                        case "project": return <span key={col.key} className="truncate text-[11px] px-1 text-muted-foreground" title={it.project_name}>{it.project_name || (it.project_names || [])[0] || "—"}</span>;
                        case "package": return <span key={col.key} className="truncate text-[11px] px-1 text-muted-foreground" title={it.package_name}>{it.package_name || "—"}</span>;
                        case "category": return (
                          <span key={col.key} className="truncate text-[11px] px-1 inline-flex items-center gap-1" title={it.category}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: catColor(it.category) }} />
                            <span className="truncate">{it.category || "—"}</span>
                          </span>
                        );
                        // Feb 2026 · Topic column — bold emerald when
                        // linked to a bucket topic, italic muted
                        // otherwise. Matches the hover-card + Tasks
                        // list treatment so the whole app reads the
                        // same way.
                        case "topic": {
                          const linkedTopic = it.linked_bucket?.topic_title;
                          const topic = linkedTopic || it.topic;
                          return (
                            <span key={col.key}
                                   className={`truncate text-[11px] px-1 ${
                                     linkedTopic ? "font-bold text-emerald-800"
                                     : topic ? "font-semibold italic text-muted-foreground"
                                     : "text-muted-foreground"
                                   }`}
                                   title={topic ? (linkedTopic ? `Topic: ${topic}` : `Free topic: ${topic}`) : "No topic"}
                                   data-testid={`cc-gantt-topic-${it.id}`}>
                              {topic || "—"}
                            </span>
                          );
                        }
                        // R268aaaa · Feb 2026 — Inline editable Deliverable Status
                        case "del_status": return (
                          <select key={col.key}
                                  value={it.status || "planned"}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => { e.stopPropagation(); patchItem(it.id, { status: e.target.value }); }}
                                  className="mx-1 h-5 text-[10px] bg-transparent border border-border/70 rounded-sm px-1"
                                  data-testid={"cc-gantt-del-status-" + it.id}>
                            {ITEM_STATUSES.map((s) => <option key={s} value={s}>{ITEM_STATUS_LABEL[s] || s}</option>)}
                          </select>
                        );
                        // R268aaaa · Inline editable Deliverable Date
                        case "del_date": return (
                          <input key={col.key} type="date"
                                 value={it.planned_date || ""}
                                 onClick={(e) => e.stopPropagation()}
                                 onChange={(e) => { e.stopPropagation(); patchItem(it.id, { planned_date: e.target.value }); }}
                                 className="mx-1 h-5 text-[10px] bg-transparent border border-border/70 rounded-sm px-1 w-full min-w-0"
                                 data-testid={"cc-gantt-del-date-" + it.id} />
                        );
                        // Task columns are not applicable on the item row.
                        case "task_status":
                        case "task_start":
                        case "task_due":
                          return <span key={col.key} className="text-muted-foreground/40 text-[10px] px-1">—</span>;
                        default: return <span key={col.key} />;
                      }
                    })}
                  </div>
                );
              }
              // Task row
              const t = row.task;
              const parent = row.parent;
              return (
                <div key={row.key}
                     className="grid items-center border-b border-border/40 hover:bg-primary/5 cursor-pointer bg-muted/10"
                     style={{ height: ROW_H, gridTemplateColumns: gridTemplate }}
                     data-testid={"cc-gantt-row-task-" + t.id}
                     onClick={() => onOpenTask && onOpenTask(t.id)}>
                  <div />
                  {visibleCols.map((col) => {
                    switch (col.key) {
                      case "title": return (
                        <div key={col.key} className="min-w-0 flex items-center gap-1 pl-3 pr-1 group/task">
                          <span className="inline-block w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: taskColor(t.status) }} />
                          <span className="text-muted-foreground/60 text-[10px] shrink-0">└</span>
                          <span className="truncate text-[11px]" title={t.title}>{t.title || "Task"}</span>
                          {/* R268aaaa · scroll-to task start date */}
                          <button type="button"
                                  onClick={(e) => { e.stopPropagation(); scrollToDate(t.planned_start_date || t.start_date || parent.planned_date); }}
                                  className="ml-auto opacity-0 group-hover/task:opacity-70 hover:!opacity-100 text-primary shrink-0"
                                  title={"Scroll to " + (t.planned_start_date || t.start_date || parent.planned_date || "—")}
                                  data-testid={"cc-gantt-goto-task-" + t.id}>
                            <ChevronsRight className="w-3 h-3" />
                          </button>
                        </div>
                      );
                      case "primary": return (
                        <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground/70" title={parent.primary_assignee_name || "Unassigned"}>
                          {parent.primary_assignee_name || "—"}
                        </span>
                      );
                      case "assignee": return (
                        <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground" title={t.assignee_name || "Unassigned"}>
                          {t.assignee_name || "—"}
                        </span>
                      );
                      case "brand":   return <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground" title={parent.brand_name}>{parent.brand_name || "—"}</span>;
                      case "client":  return <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground" title={parent.client_name}>{parent.client_name || "—"}</span>;
                      case "project": return <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground" title={t.project_name || parent.project_name}>{t.project_name || parent.project_name || "—"}</span>;
                      case "package": return <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground" title={parent.package_name}>{parent.package_name || "—"}</span>;
                      case "category": return (
                        <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground inline-flex items-center gap-1" title={parent.category}>
                          <span className="inline-block w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: catColor(parent.category) }} />
                          <span className="truncate">{parent.category || "—"}</span>
                        </span>
                      );
                      // Feb 2026 · Task row inherits deliverable topic read-only.
                      case "topic": {
                        const linkedTopic = parent.linked_bucket?.topic_title;
                        const topic = linkedTopic || parent.topic;
                        return (
                          <span key={col.key}
                                 className={`truncate text-[10px] px-1 ${
                                   linkedTopic ? "font-bold text-emerald-800/80"
                                   : topic ? "italic text-muted-foreground"
                                   : "text-muted-foreground/60"
                                 }`}
                                 title={topic || "—"}>
                            {topic || "—"}
                          </span>
                        );
                      }
                      // R268aaaa · deliverable-level columns inherited read-only on task rows
                      case "del_status":
                        return <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground/60">{ITEM_STATUS_LABEL[parent.status] || parent.status || "—"}</span>;
                      case "del_date":
                        return <span key={col.key} className="truncate text-[10px] px-1 text-muted-foreground/60">{parent.planned_date || "—"}</span>;
                      // R268aaaa · Inline editable Task Status
                      case "task_status": return (
                        <select key={col.key}
                                value={t.status || "open"}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => { e.stopPropagation(); patchTask(t.id, { status: e.target.value }); }}
                                className="mx-1 h-5 text-[10px] bg-transparent border border-border/70 rounded-sm px-1"
                                data-testid={"cc-gantt-task-status-" + t.id}>
                          {TASK_STATUSES.map((s) => <option key={s} value={s}>{TASK_STATUS_LABEL[s] || s}</option>)}
                        </select>
                      );
                      // R268aaaa · Inline editable Task Start Date
                      case "task_start": return (
                        <input key={col.key} type="date"
                               value={t.planned_start_date || t.start_date || ""}
                               onClick={(e) => e.stopPropagation()}
                               onChange={(e) => { e.stopPropagation(); patchTask(t.id, { start_date: e.target.value }); }}
                               className="mx-1 h-5 text-[10px] bg-transparent border border-border/70 rounded-sm px-1 w-full min-w-0"
                               data-testid={"cc-gantt-task-start-" + t.id} />
                      );
                      // R268aaaa · Inline editable Task Due Date
                      case "task_due": return (
                        <input key={col.key} type="date"
                               value={t.due_date || ""}
                               onClick={(e) => e.stopPropagation()}
                               onChange={(e) => { e.stopPropagation(); patchTask(t.id, { due_date: e.target.value }); }}
                               className="mx-1 h-5 text-[10px] bg-transparent border border-border/70 rounded-sm px-1 w-full min-w-0"
                               data-testid={"cc-gantt-task-due-" + t.id} />
                      );
                      default: return <span key={col.key} />;
                    }
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* R268aaaa · Feb 2026 — Draggable divider between the two panes.
            Lets a PM widen the left grid to see the new inline-editable
            columns without needing to toggle any Column visibility. */}
        <div className={"relative w-1.5 shrink-0 bg-border/70 hover:bg-primary/70 cursor-col-resize transition-colors " + (paneResizing ? "bg-primary/70" : "")}
             onPointerDown={onPaneResizeStart}
             data-testid="cc-gantt-pane-resize"
             title="Drag to resize panes"
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
        </div>

        {/* RIGHT PANE (timeline) */}
        <div ref={scrollRef} id="cc-gantt-right-scroll" className="overflow-auto flex-1 relative"
             onScroll={(e) => {
               // Keep left pane's vertical scroll in sync with the right pane.
               const left = document.getElementById("cc-gantt-left-scroll");
               if (left && Math.abs(left.scrollTop - e.currentTarget.scrollTop) > 0.5) {
                 left.scrollTop = e.currentTarget.scrollTop;
               }
             }}
             style={{ maxHeight: full ? "calc(100vh - 64px)" : "72vh" }}
             data-testid="cc-gantt-timeline">
          <div style={{ width: totalGridW, minWidth: totalGridW }}>
            {/* Header rows: month band + day-number band */}
            <div className="sticky top-0 z-20 bg-muted/60 border-b border-border" style={{ height: HEADER_H }}>
              <div className="flex" style={{ height: HEADER_H / 2 }}>
                {monthGroups.map((g) => (
                  <div key={g.k} className="border-r border-border/60 flex items-center justify-center text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40"
                       style={{ width: g.count * DAY_W }}>{g.label}</div>
                ))}
              </div>
              <div className="flex" style={{ height: HEADER_H / 2 }}>
                {ZOOM_PRESETS[zoom].showDayNumbers ? days.map((d, i) => {
                  const m = _dayMeta(d);
                  const bg = _dayBg(m, 1);
                  const isToday = isSameDay(d, today);
                  const specialTitle = m.specials.length ? m.specials.map((s) => (s.emoji ? s.emoji + " " : "") + s.name).join(" · ") : null;
                  return (
                    <div key={i}
                         className={"border-r border-border/40 flex flex-col items-center justify-center leading-none " +
                                    (isToday ? "bg-primary/20 text-primary font-bold" : (m.isWkend ? "text-muted-foreground/70" : "text-muted-foreground"))}
                         style={{ width: DAY_W, ...(isToday ? {} : (bg ? { backgroundColor: bg } : {})) }}
                         title={specialTitle || undefined}
                         data-testid={m.isSpecial ? "cc-gantt-day-special-" + m.key : undefined}>
                      <span className="text-[8px] uppercase tracking-widest opacity-70">
                        {["S","M","T","W","T","F","S"][d.getDay()]}
                      </span>
                      <span className="text-[10px] font-semibold flex items-center gap-0.5">
                        {d.getDate()}
                        {m.specials.length > 0 && m.specials[0].emoji && (
                          <span className="text-[9px]">{m.specials[0].emoji}</span>
                        )}
                      </span>
                    </div>
                  );
                }) : (
                  // R268hhh/R268jjj — Week / Month zoom. Draw thin
                  // vertical rules on weekends & holiday days so PMs
                  // still see the shape of the week even when day
                  // numbers are too small to render. Monday gets a
                  // stronger separator.
                  days.map((d, i) => {
                    const m = _dayMeta(d);
                    const isToday = isSameDay(d, today);
                    const bg = _dayBg(m, 0.55);
                    return (
                      <div key={i}
                           className={"flex items-stretch " +
                                      (isToday ? "bg-primary/25" :
                                       d.getDay() === 1 ? "border-l border-border/60" : "")}
                           style={{ width: DAY_W, ...(bg && !isToday ? { backgroundColor: bg } : {}) }}
                           title={m.specials.length ? m.specials.map((s) => s.name).join(" · ") : undefined}
                           data-testid={m.isSpecial ? "cc-gantt-day-special-" + m.key : undefined} />
                    );
                  })
                )}
              </div>
            </div>

            {/* Body rows */}
            <div className="relative">
              {/* R268jjj · Column shading — weekend / special / both.
                  Rendered as absolute overlays under the bars so the
                  pattern reads top-to-bottom across every row. Also
                  drops a thin left tick on every Monday to preserve
                  the shape of the week at Week/Month zoom levels. */}
              <div className="absolute inset-0 pointer-events-none">
                {days.map((d, i) => {
                  const m = _dayMeta(d);
                  const bg = _dayBg(m, 0.5);
                  if (!bg && d.getDay() !== 1) return null;
                  return (
                    <React.Fragment key={i}>
                      {bg && (
                        <div className="absolute top-0 bottom-0"
                             style={{ left: i * DAY_W, width: DAY_W, backgroundColor: bg }} />
                      )}
                      {d.getDay() === 1 && (
                        <div className="absolute top-0 bottom-0 border-l border-border/40"
                             style={{ left: i * DAY_W }} />
                      )}
                    </React.Fragment>
                  );
                })}
                {todayIdx >= 0 && (
                  <div className="absolute top-0 bottom-0 border-l-2 border-primary/70"
                       style={{ left: todayIdx * DAY_W + DAY_W / 2 }} />
                )}
              </div>

              {/* R268kkk · Task spans → resize handles. Renders each
                  bar with left/right start/end coordinates, dragging
                  either handle patches start_date / due_date; the
                  middle area still slides the whole bar. */}
              {(() => {
                // Build row-index-by-id map for dependency arrows.
                const rowIdxById = {};
                rows.forEach((r, i) => {
                  const id = r.kind === "item" ? r.it.id : r.task.id;
                  rowIdxById[id] = i;
                });
                return null;
              })()}

              {rows.map((row, rowIdx) => {
                const span = _rowSpan(row);
                const dtStart = parseISO(span.start);
                const dtEnd = parseISO(span.end);
                if (!dtStart || !dtEnd) {
                  return <div key={row.key} className="relative border-b border-border/40" style={{ height: ROW_H }} />;
                }
                const startIdx = daysBetween(range.start, dtStart);
                const endIdx = daysBetween(range.start, dtEnd);
                const inRange = endIdx >= 0 && startIdx < range.days;
                const isDragging = drag && drag.rowKey === row.key;
                // During a drag, visually offset by deltaDays. Which
                // edges move depends on the drag mode.
                let sIdx = startIdx, eIdx = endIdx;
                if (isDragging) {
                  if (drag.mode === "move") { sIdx += drag.deltaDays; eIdx += drag.deltaDays; }
                  else if (drag.mode === "resize-left") sIdx = Math.min(eIdx, sIdx + drag.deltaDays);
                  else if (drag.mode === "resize-right") eIdx = Math.max(sIdx, eIdx + drag.deltaDays);
                }
                const bg = row.kind === "item" ? catColor(row.it.category) : taskColor(row.task.status);
                const barH = row.kind === "item" ? BAR_H_ITEM : BAR_H_TASK;
                const isSpan = row.kind === "task" && span.start && span.end && span.start !== span.end;
                const barLeft = sIdx * DAY_W + (isSpan ? 2 : (DAY_W - (DAY_W - 4)) / 2);
                const barWidth = isSpan
                  ? Math.max(6, (eIdx - sIdx + 1) * DAY_W - 4)
                  : (row.kind === "item" ? DAY_W - 4 : DAY_W - 8);
                const barLeftPoint = row.kind === "item"
                  ? sIdx * DAY_W + 2
                  : sIdx * DAY_W + 4;
                const finalLeft = isSpan ? barLeft : barLeftPoint;
                // R268ooo · Feb 2026 — Baseline (ghost) span. Only
                // meaningful for tasks that have been rescheduled at
                // least once — the backend snapshots `baseline_*`
                // fields on the first real date change. When present
                // and different from the current planned span, we
                // paint a dashed outline behind the solid bar so PMs
                // see how far the task has slipped.
                let ghost = null;
                if (row.kind === "task") {
                  const bs = row.task.baseline_start_date;
                  const be = row.task.baseline_due_date;
                  if (bs && be && (bs !== span.start || be !== span.end)) {
                    const dtBs = parseISO(bs);
                    const dtBe = parseISO(be);
                    if (dtBs && dtBe && dtBs <= dtBe) {
                      const bsIdx = daysBetween(range.start, dtBs);
                      const beIdx = daysBetween(range.start, dtBe);
                      if (beIdx >= 0 && bsIdx < range.days) {
                        const gLeft = bsIdx * DAY_W + 2;
                        const gWidth = Math.max(6, (beIdx - bsIdx + 1) * DAY_W - 4);
                        // Slippage direction — if the current span
                        // starts AFTER the baseline, we've slipped
                        // forward (worse); paint a warmer amber.
                        // If it starts earlier (rare — we pulled it
                        // in) paint a cooler teal.
                        const slipped = parseISO(span.end) > dtBe;
                        ghost = {
                          left: gLeft, width: gWidth,
                          stroke: slipped ? "#f59e0b" : "#0d9488",
                          title: `Baseline: ${bs} → ${be}`,
                        };
                      }
                    }
                  }
                }
                const label = row.kind === "item"
                  ? (row.it.topic || row.it.name || row.it.title || "Deliverable")
                  : (row.task.title || "Task");
                const dragPreview = isDragging ? (
                  drag.mode === "resize-left" ? `Start → ${iso(addDays(parseISO(span.start), drag.deltaDays))}`
                  : drag.mode === "resize-right" ? `Due → ${iso(addDays(parseISO(span.end), drag.deltaDays))}`
                  : `${iso(addDays(parseISO(span.end), drag.deltaDays))}`
                ) : null;
                return (
                  <div key={row.key} className="relative border-b border-border/40"
                       style={{ height: ROW_H }}
                       data-row-index={rowIdx}>
                    {/* Ghost baseline bar — dashed outline, no bg fill,
                        rendered before the solid bar so it sits
                        underneath and pointer events pass through. */}
                    {ghost && (
                      <div
                        className="absolute rounded-sm pointer-events-none"
                        style={{
                          top: (ROW_H - BAR_H_TASK) / 2 - 1,
                          left: ghost.left,
                          width: ghost.width,
                          height: BAR_H_TASK + 2,
                          border: `1.5px dashed ${ghost.stroke}`,
                          backgroundColor: "transparent",
                          opacity: 0.85,
                        }}
                        title={ghost.title}
                        data-testid={"cc-gantt-ghost-" + row.task.id}
                      />
                    )}
                    {inRange && (
                      <div
                        role="button"
                        tabIndex={0}
                        onPointerDown={(e) => onBarPointerDown(e, row, "move")}
                        className={"absolute rounded-sm cursor-grab active:cursor-grabbing shadow-sm hover:brightness-110 border border-white/40 " + (isDragging ? "opacity-70 ring-2 ring-primary/70" : "")}
                        style={{
                          top: (ROW_H - barH) / 2,
                          left: finalLeft,
                          width: barWidth,
                          height: barH,
                          backgroundColor: bg,
                        }}
                        title={label + " · " + (isSpan ? `${span.start} → ${span.end}` : span.end) + (dragPreview ? `  · ${dragPreview}` : "")}
                        data-testid={"cc-gantt-bar-" + (row.kind === "item" ? row.it.id : row.task.id)}
                      />
                    )}
                    {/* Resize handles — only for task spans. Rendered
                        AFTER the bar so they sit on top and receive
                        pointer events first. */}
                    {inRange && isSpan && (
                      <>
                        <div
                          onPointerDown={(e) => onBarPointerDown(e, row, "resize-left")}
                          className="absolute cursor-ew-resize hover:bg-primary/70"
                          style={{
                            top: (ROW_H - barH) / 2,
                            left: finalLeft,
                            width: RESIZE_HANDLE_W,
                            height: barH,
                            backgroundColor: "rgba(255,255,255,0.35)",
                            borderLeft: "2px solid rgba(0,0,0,0.35)",
                          }}
                          title="Drag to change start date"
                          data-testid={"cc-gantt-resize-l-" + row.task.id}
                        />
                        <div
                          onPointerDown={(e) => onBarPointerDown(e, row, "resize-right")}
                          className="absolute cursor-ew-resize hover:bg-primary/70"
                          style={{
                            top: (ROW_H - barH) / 2,
                            left: finalLeft + barWidth - RESIZE_HANDLE_W,
                            width: RESIZE_HANDLE_W,
                            height: barH,
                            backgroundColor: "rgba(255,255,255,0.35)",
                            borderRight: "2px solid rgba(0,0,0,0.35)",
                          }}
                          title="Drag to change due date"
                          data-testid={"cc-gantt-resize-r-" + row.task.id}
                        />
                      </>
                    )}
                    {/* Drag ghost — target-day preview column */}
                    {isDragging && inRange && drag.deltaDays !== 0 && (
                      <div className="absolute pointer-events-none top-0 bottom-0 bg-primary/10 border-l-2 border-primary/60"
                           style={{
                             left: (drag.mode === "resize-left" ? sIdx : eIdx) * DAY_W,
                             width: DAY_W,
                           }}>
                        <div className="text-[9px] text-primary font-semibold text-center pt-0.5 whitespace-nowrap px-1">
                          {dragPreview && dragPreview.slice(-5)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* R268lll · Dependency arrows — SVG overlay stitching
                  predecessor task's due-edge to successor task's
                  start-edge. Only tasks currently rendered as rows
                  produce arrows so we don't draw into empty space. */}
              {(() => {
                const rowIdxById = {};
                rows.forEach((r, i) => {
                  const id = r.kind === "item" ? r.it.id : r.task.id;
                  rowIdxById[id] = i;
                });
                const arrows = [];
                rows.forEach((r, i) => {
                  if (r.kind !== "task") return;
                  const deps = r.task.depends_on_task_ids || [];
                  deps.forEach((pid) => {
                    const pi = rowIdxById[pid];
                    if (pi === undefined) return;
                    const predRow = rows[pi];
                    if (predRow.kind !== "task") return;
                    const predSpan = _rowSpan(predRow);
                    const succSpan = _rowSpan(r);
                    const predEnd = parseISO(predSpan.end);
                    const succStart = parseISO(succSpan.start);
                    if (!predEnd || !succStart) return;
                    const predEndIdx = daysBetween(range.start, predEnd);
                    const succStartIdx = daysBetween(range.start, succStart);
                    // Skip if either end is outside the visible range —
                    // an arrow into a cropped void just adds visual
                    // noise.
                    if (predEndIdx < 0 || succStartIdx >= range.days) return;
                    const x1 = predEndIdx * DAY_W + DAY_W - 2;
                    const y1 = pi * ROW_H + ROW_H / 2;
                    const x2 = succStartIdx * DAY_W + 2;
                    const y2 = i * ROW_H + ROW_H / 2;
                    // Elbow path: right from pred → down (or up) →
                    // right into succ. If x2 < x1 (loop back), route
                    // out further and around underneath the pred row.
                    const gap = 8;
                    const midX = x2 > x1 + gap ? (x1 + x2) / 2 : x1 + gap;
                    // Slack — a rendered warning class for late deps
                    const isViolated = predEnd > succStart;
                    arrows.push({ key: pid + "→" + r.task.id, x1, y1, x2, y2, midX, isViolated });
                  });
                });
                if (!arrows.length) return null;
                return (
                  <svg
                    className="absolute inset-0 pointer-events-none"
                    style={{ width: totalGridW, height: rows.length * ROW_H }}
                    data-testid="cc-gantt-arrows">
                    <defs>
                      <marker id="cc-gantt-arrowhead" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                        <path d="M0,0 L8,4 L0,8 z" fill="#0284c7" />
                      </marker>
                      <marker id="cc-gantt-arrowhead-warn" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                        <path d="M0,0 L8,4 L0,8 z" fill="#dc2626" />
                      </marker>
                    </defs>
                    {arrows.map((a) => {
                      const stroke = a.isViolated ? "#dc2626" : "#0284c7";
                      const marker = a.isViolated ? "url(#cc-gantt-arrowhead-warn)" : "url(#cc-gantt-arrowhead)";
                      const d = `M ${a.x1} ${a.y1} H ${a.midX} V ${a.y2} H ${a.x2}`;
                      return (
                        <path key={a.key} d={d}
                              stroke={stroke} strokeWidth="1.2" fill="none" opacity="0.85"
                              markerEnd={marker}
                              data-testid={"cc-gantt-arrow-" + a.key} />
                      );
                    })}
                  </svg>
                );
              })()}

              {rows.length === 0 && (
                <div className="h-40" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer legend */}
      <div className="flex items-center gap-4 text-[10px] text-muted-foreground border-t border-border px-3 py-1.5 flex-wrap">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#0ea5e9" }} /> Deliverable</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#94a3b8" }} /> Task · todo</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#0284c7" }} /> in progress</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#10b981" }} /> done</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#ef4444" }} /> blocked</span>
        <span className="ml-auto">Drag to move / resize · arrows show dependencies (red = late) · dashed outline = baseline (amber = slipped)</span>
      </div>
    </div>
  );
}
